<template>
    <div>
        <template v-if="loading">
            <loading-spinner />
        </template>

        <template v-else>
            <x-container fluid app-width>
                <x-layout my-5>
                    <x-flex xs12>
                        <h1>Insurer entity</h1>
                    </x-flex>
                </x-layout>

                <x-layout>
                    <x-flex>
                        <x-container fluid class="summary-container">
                            <x-layout>
                                <x-flex xs12>
                                    <x-autocomplete
                                        v-model="selectedInsurerId"
                                        @update:searchInput="insurerSearchUpdated"
                                        :filter="insurerSearchFilter"
                                        :items="insurersFiltered"
                                        item-text="name"
                                        item-value="id"
                                        label="Search for insurer"
                                        placeholder=" "
                                        append-icon="search"
                                        clearable
                                        hide-details
                                    >
                                        <template v-slot:no-data>
                                            <x-list-tile>
                                                <x-list-tile-title>
                                                    Enter query to start search...
                                                </x-list-tile-title>
                                            </x-list-tile>
                                        </template>

                                        <template v-slot:item="{ item }">
                                            <x-list-tile-content>
                                                <x-list-tile-title v-text="item.name" class="insurer-search-name"/>
                                                <x-list-tile-sub-title v-text="item.address?.fullAddress || ''"/>
                                            </x-list-tile-content>
                                        </template>
                                    </x-autocomplete>
                                </x-flex>
                                <x-spacer/>
                            </x-layout>

                            <template v-if="selectedInsurer">
                                <x-layout align-center my-5>
                                    <x-flex xs10>
                                        <h3>{{ selectedInsurer.name }}</h3>

                                        <p>{{  selectedInsurer.address?.fullAddress }}</p>
                                    </x-flex>

                                    <x-flex xs2 text-xs-right>
                                        <x-btn
                                            :disabled="!selectedInsurer"
                                            @click="openSecurityModal(-1)"
                                        >
                                            Add new security details
                                        </x-btn>
                                    </x-flex>
                                </x-layout>

                                <x-layout>
                                    <x-flex>
                                        <x-data-table
                                            class="no-row-hover mt-4"
                                            :headers="headers"
                                            :items="filteredSecurities"
                                            hide-actions
                                        >
                                            <template v-slot:no-data>
                                                <p class="error-message text-xs-center">No securities found</p>
                                            </template>

                                            <template v-slot:items="props">
                                                <td>
                                                    {{ props.item.name }}
                                                </td>

                                                <td>
                                                    {{ getSecurityRating(props.item?.strength_rating, props.item?.rating_suffix) }}
                                                </td>

                                                <td>
                                                    {{ getMaxLine(props.item?.max_line_size, props.item?.max_line_currency) }}
                                                </td>

                                                <td>
                                                    {{ getMaxTenor(props.item?.max_tenor) }}
                                                </td>

                                                <td class="text-xs-right">
                                                    <x-btn
                                                        class="ma-0"
                                                        icon
                                                        color="tertiary"
                                                        @click="openSecurityModal(props.item.id)"
                                                    >
                                                        <x-icon>edit</x-icon>
                                                    </x-btn>
                                                </td>
                                            </template>
                                        </x-data-table>
                                    </x-flex>
                                </x-layout>
                            </template>
                        </x-container>
                    </x-flex>
                </x-layout>
            </x-container>
        </template>

        <x-dialog
            v-model="showInsurerEntityModal"
            v-if="showInsurerEntityModal"
            persistent
            max-width="700px"
        >
            <x-container fluid>
                <x-layout>
                    <x-flex>
                        <h2>{{ editableSecurity.id >= 0 ? 'Edit' : 'Add'}} insurer security</h2>
                    </x-flex>
                </x-layout>

                <x-layout mt-4 style="text-align: left;">
                    <x-flex>
                        <data-list :value="addressData" />
                    </x-flex>
                </x-layout>

                <x-form class="mt-4" @submit:prevent="" ref="insurerForm">
                    <x-layout>
                        <x-flex xs12>
                            <x-text-field
                                v-model="editableSecurity.name"
                                label="Security"
                                placeholder=" "
                                :rules="[v => ruleRequired(v, 'Security')]"
                            />
                        </x-flex>
                    </x-layout>

                    <x-layout mt-3>
                        <x-flex xs6>
                            <x-select
                                :items="['AAA', 'AA', 'A', 'BBB', 'BB', 'B', 'CCC', 'CC', 'C', 'D', 'NR']"
                                v-model="editableSecurity.strength_rating"
                                label="S&P security rating"
                                :clearable="true"
                                placeholder=" "
                                append-icon="keyboard_arrow_down"
                                attach
                            />
                        </x-flex>

                        <x-flex xs6>
                            <x-select
                                :items="['+', '-']"
                                :disabled="!this.editableSecurity.strength_rating || ['D', 'NR'].includes(this.editableSecurity.strength_rating)"
                                v-model="editableSecurity.rating_suffix"
                                label="S&P security rating suffix"
                                :clearable="true"
                                placeholder=" "
                                append-icon="keyboard_arrow_down"
                                attach
                            />
                        </x-flex>
                    </x-layout>

                    <x-layout mt-3>
                        <x-flex xs6>
                            <x-layout>
                                <x-flex xs4 pr-0>
                                    <x-select
                                        :items="['USD', 'GBP']"
                                        v-model="editableSecurity.max_line_currency"
                                        label="Max line size"
                                        placeholder=" "
                                        append-icon="keyboard_arrow_down"
                                        attach
                                    />
                                </x-flex>

                                <x-flex xs8>
                                    <x-currency-text-field
                                        v-model="editableSecurity.max_line_size"
                                        label=" "
                                        placeholder=" "
                                        :rules="[
                                            v => ruleLessThanOrEqualTo(v, 999999999),
                                            v => ruleGreaterThan(v, 0)
                                        ]"
                                        :applyNumericRule="!(editableSecurity.max_line_size === undefined || editableSecurity.max_line_size === '')"
                                    />
                                </x-flex>
                            </x-layout>
                        </x-flex>

                        <x-flex xs6>
                            <x-text-field
                                v-model="editableSecurity.max_tenor"
                                label="Max tenor"
                                placeholder=" "
                                :rules="[
                                    v => ruleLessThanOrEqualTo(v, 9999),
                                    v => ruleGreaterThan(v, 0)
                                ]"
                                suffix="years"
                            />
                        </x-flex>
                    </x-layout>

                    <x-layout mt-3 v-for="(identifier, index) in identifiers" :key="index">
                        <x-flex xs6>
                            <x-autocomplete
                                v-model="identifier.type"
                                :items="identifierTypes"
                                class="required"
                                label="Identifier type"
                                placeholder="Identifier type"
                                item-value="type"
                                item-text="name"
                                :rules="[
                                    v => ruleRequired(v, 'Identifier type'),
                                    v => identifiers.filter(({ type }) => type === v).length > 1 ? 'Identifier type already in use' : true
                                ]"
                                append-icon="keyboard_arrow_down"
                                outline
                                attach
                            />
                        </x-flex>

                        <x-flex>
                            <x-text-field
                                v-model="identifier.value"
                                class="required"
                                label="Identifier"
                                placeholder=" "
                                :rules="[v => ruleRequired(v, 'Identifier')]"
                            />
                        </x-flex>

                        <x-flex class="grow-0">
                            <x-btn
                                class="square-icon label-spacing"
                                color="tertiary"
                                small
                                @click="removeIdentifier(index)"
                            >
                                <i class="material-icons-outlined">delete_forever</i>
                            </x-btn>
                        </x-flex>
                    </x-layout>

                    <x-layout class="response-actions" mt-4 justify-space-between>
                        <x-flex xs6 class="text-xs-left">
                            <x-btn
                                class="ma-0"
                                color="tertiary"
                                small
                                @click="showInsurerEntityModal = false"
                            >
                                Cancel
                            </x-btn>
                        </x-flex>

                        <x-flex xs6 class="text-xs-right">
                            <x-btn
                                class="mr-4"
                                color="tertiary"
                                small
                                @click="addIdentifier"
                                :disabled="identifiers.length >= identifierTypes.length"
                            >
                                Add identifier
                            </x-btn>

                            <x-btn
                                color="primary"
                                small
                                @click="submitInsurerSecurity"
                                :loading="insurerSecurityLoading"
                            >
                                Submit
                            </x-btn>
                        </x-flex>
                    </x-layout>
                </x-form>
            </x-container>
        </x-dialog>
        <modal
            ref="confirmEdit"
            title="Confirm edit"
            confirmation
        >
            <div class="text-xs-left">
                <p>
                    Editing this security will apply the changes retroactively to all risks using this security.
                </p>
                <p>
                    Are you sure you want to make this change?
                </p>
            </div>
        </modal>
    </div>
</template>

<script>
import Modal from '~/components/Shared/UIComponents/Modal';
import DataList from '~/components/Shared/UIComponents/DataList';
import rules from '~/mixins/rules';

export default {
    name: "InsurerEntity",
    components: {
        Modal,
        DataList
    },
    mixins: [rules],
    data() {
        return {
            loading: true,
            insurers: [],
            insurersFiltered: [],
            securities: [],
            filteredSecurities: [],
            selectedInsurerId: null,
            searchStringLenght: -1,
            selectedInsurer: null,
            editableSecurity: {},
            showInsurerEntityModal: false,
            insurerSecurityLoading: false,
            identifiers: [],
            identifierTypes: [
                {
                    type: 'lei',
                    field: 'legal_entity_identifier',
                    name: 'Legal Entity Identifier (LEI)',
                },
                {
                    type: 'duns',
                    field: 'dun_and_bradstreet_identifier',
                    name: 'Dun & Bradstreet (D-U-N-S)',
                },
                {
                    type: 'ein',
                    field: 'employer_identification_number_identifier',
                    name: 'Employer Identification Number (EIN)',
                },
                {
                    type: 'syn',
                    field: 'syndicate_number_identifier',
                    name: 'Syndicate Number',
                },
                {
                    type: 'bnpp',
                    field: 'bnpp_technical_identifier',
                    name: 'BNPP Technical ID',
                },
            ],
            headers: [
                {
                    text: 'Security',
                    value: 'name'
                },
                {
                    text: 'S&P security rating',
                    value: 'security_rating'
                },
                {
                    text: 'Max line size',
                    value: 'line_size'
                },
                {
                    text: 'max tenor',
                    value: 'max_tenor'
                },
                {
                    // edit security
                    sortable: false
                }
            ],
            addressData: [],
        }
    },
    created() {
        let insurerRequest = axios.get(this.$root.api('insurer') + '?withAddress=true', {responseType: 'json'});
        insurerRequest.then(res => { this.insurers = res.data.data; });
        let securityRequest = axios.get(this.$root.api('broker/security'));
        securityRequest.then(res => { this.securities = res.data.data; });
        Promise.all([insurerRequest, securityRequest]).then(() => {
            this.loading = false;
        });
    },
    watch: {
        selectedInsurerId() {
            if (this.selectedInsurerId) {
                this.selectedInsurer = this.insurers.find(insurer => insurer.id === this.selectedInsurerId);
                this.filteredSecurities = this.securities.filter(security => security.insurer_company_id === this.selectedInsurerId);
                this.selectedInsurerId = undefined;
                this.insurersFiltered = [];
            }
        },
        "editableSecurity.strength_rating": {
            handler() {
                if (!this.editableSecurity.strength_rating || ['D', 'NR'].includes(this.editableSecurity.strength_rating)) {
                    this.editableSecurity.rating_suffix = undefined;
                }
            }
        }
    },
    methods: {
        addIdentifier() {
            this.identifiers.push({});
        },
        removeIdentifier(index) {
            this.identifiers.splice(index, 1);
        },
        insurerSearchUpdated(event) {
            if (this.searchStringLenght === event?.length) {
                return;
            }

            if (event?.length > 0 && this.insurersFiltered.length === 0) {
                this.insurersFiltered = this.insurers.map(insurer => ({
                    ...insurer,
                    searchValue: insurer.name + (insurer.address?.line1 || '')
                }));
            } else if (event?.length === 0 && this.insurersFiltered.length > 0) {
                this.insurersFiltered = [];
            }
        },
        insurerSearchFilter(item, queryText, itemText) {
            return item.searchValue.toLocaleLowerCase().indexOf(queryText.toLocaleLowerCase()) > -1;
        },
        getSecurityRating(rating, suffix) {
            if (!rating) {
                return 'Not added';
            }

            return rating + (suffix || '');
        },
        getMaxLine(maxLine, maxLineCurrency) {
            if (!maxLine) {
                return 'Not added';
            }

            return `${maxLine} ${maxLineCurrency}`;
        },
        getMaxTenor(maxTenor) {
            if (!maxTenor) {
                return 'Not added';
            }

            return `${maxTenor} years`;
        },
        openSecurityModal(securityId) {
            this.addressData = [
                {
                    text: 'Insurer name',
                    value: this.selectedInsurer.name
                }
            ];

            if (this.selectedInsurer?.address?.line1) {
                this.addressData.push({
                    text: 'Address line 1',
                    value: this.selectedInsurer?.address?.line1
                });
            }

            if (this.selectedInsurer?.address?.line2) {
                this.addressData.push({
                    text: 'Address line 2',
                    value: this.selectedInsurer?.address?.line2
                });
            }

            if (this.selectedInsurer?.address?.city) {
                this.addressData.push({
                    text: 'Town/City',
                    value: this.selectedInsurer?.address?.city
                });
            }

            if (this.selectedInsurer?.address?.city) {
                this.addressData.push({
                    text: 'Region/State',
                    value: this.selectedInsurer?.address?.region
                });
            }

            if (this.selectedInsurer?.address?.postcode) {
                this.addressData.push({
                    text: 'Postcode',
                    value: this.selectedInsurer?.address?.postcode
                });
            }

            if (this.selectedInsurer?.address?.country_id) {
                this.addressData.push({
                    text: 'Country',
                    value: this.selectedInsurer?.address?.country?.name
                });
            }

            const securityData = this.filteredSecurities.find(security => security.id === securityId);

            this.editableSecurity = {
                id: securityId,
                insurer_company_id: this.selectedInsurer.id,
                name: securityData?.name || '',
                strength_rating: securityData?.strength_rating || undefined,
                rating_suffix: securityData?.rating_suffix || undefined,
                max_line_currency: securityData?.max_line_currency || 'GBP',
                max_line_size: securityData?.max_line_size || undefined,
                max_tenor: securityData?.max_tenor || undefined
            };

            this.identifiers = this.identifierTypes
                .map(({ type, field }) => ({
                    type,
                    value: securityData && securityData[field],
                }))
                .filter(({ value }) => value);

            this.showInsurerEntityModal = true;
        },
        async submitInsurerSecurity() {
            this.insurerSecurityLoading = true;
            if (this.$refs.insurerForm.validate()) {
                const identifiers = this.identifierTypes.reduce((acc, { type, field }) => {
                    acc[field] = this.identifiers.find(({type: t}) => t === type)?.value;

                    return acc;
                }, {});
                const payload = {
                    ...this.editableSecurity,
                    ...identifiers,
                };

                if (this.editableSecurity.id === -1) {
                    await axios.post(this.$root.api('broker/security'), payload);
                } else {
                    if (await this.$refs.confirmEdit.open() === false) {
                        this.insurerSecurityLoading = false;
                        return;
                    }
                    await axios.put(this.$root.api(`broker/security/${this.editableSecurity.id}`), payload);
                }
                this.securities = (await axios.get(this.$root.api('broker/security'))).data.data;
                this.filteredSecurities = this.securities.filter(security => security.insurer_company_id === this.selectedInsurer.id);
                this.showInsurerEntityModal = false;
            }
            this.insurerSecurityLoading = false;
        }
    }
}
</script>

<style lang="scss" scoped>
.insurer-search-name {
    color: $color-teal;
}
</style>
